import { createLogger } from './utils/logger';
import { MapView } from './components/MapView';
import { ArcLayer } from './components/ArcLayer';
import { DotLayer } from './components/DotLayer';
import { WaypointLayer } from './components/WaypointLayer';
import { TimelineSlider } from './components/TimelineSlider';
import { GenerationCapSlider } from './components/GenerationCapSlider';
import { NeedsReviewPanel } from './components/NeedsReviewPanel';
import { UploadScreen } from './components/UploadScreen';
import { BranchPanel } from './components/BranchPanel';
import { BrickWallPanel } from './components/BrickWallPanel';
import { GeocoderService } from './services/GeocoderService';
import { GraphBuilderService } from './services/GraphBuilderService';
import { BranchAssignmentService } from './services/BranchAssignmentService';
import { ArcGeneratorService } from './services/ArcGeneratorService';
import { DotAccumulatorService } from './services/DotAccumulatorService';
import { AncestorTimelineService, isEvidencePoint } from './services/AncestorTimelineService';
import { GeoCache } from './utils/GeoCache';
import { LocationNormalizer } from './utils/LocationNormalizer';
import { personDisplayName } from './utils/PersonName';
import { RateLimiter } from './utils/RateLimiter';
import { AppStore } from './store/AppStore';
import { TimelineStore } from './store/TimelineStore';
import type { AncestorGraph } from './domain/AncestorGraph';
import type { PlaceUse } from './domain/FailedPlace';
import type { Location } from './domain/Location';
import type { RawDataset } from './domain/RawDataset';
import type { TimelinePoint } from './domain/TimelinePoint';
import type { ResolvedCoords } from './services/GeocoderService';
import seedJson from './data/seed-geocache.json';
import overridesJson from './data/geocode-overrides.json';

const log = createLogger('main');

const appEl = document.getElementById('app');
const controlsEl = document.getElementById('controls');
const reviewPanelEl = document.getElementById('review-panel');
const uploadScreenEl = document.getElementById('upload-screen');
const sidebarBranchEl = document.getElementById('branch-panel-slot');
const sidebarBrickEl = document.getElementById('brick-wall-panel-slot');
if (
  !appEl ||
  !controlsEl ||
  !reviewPanelEl ||
  !uploadScreenEl ||
  !sidebarBranchEl ||
  !sidebarBrickEl
) {
  throw new Error('Missing required mount point in index.html');
}

// ── Services ──────────────────────────────────────────────────────────────────
type SeedEntry = {
  placeName: string;
  lat: number;
  lng: number;
  generatedAt: string;
  precision?: 'broader';
};
// Hand-verified overrides are applied after the seed, replace seed entries, and are
// marked verified so a place naming no region is not listed "to double-check".
const seedCache = new Map<string, ResolvedCoords>();
const toCoords = ({ lat, lng, precision }: SeedEntry): ResolvedCoords =>
  precision === 'broader' ? { lat, lng, approximate: true } : { lat, lng };
for (const entry of seedJson as SeedEntry[]) {
  seedCache.set(entry.placeName, toCoords(entry));
}
for (const entry of overridesJson as SeedEntry[]) {
  seedCache.set(entry.placeName, { ...toCoords(entry), verified: true });
}
const normalizer = new LocationNormalizer();
const geocoderService = new GeocoderService(
  seedCache,
  new GeoCache(),
  new RateLimiter(1, 1),
  normalizer,
);
const graphBuilder = new GraphBuilderService();
const branchAssigner = new BranchAssignmentService();
const arcGenerator = new ArcGeneratorService();
const dotAccumulator = new DotAccumulatorService();
const timelineService = new AncestorTimelineService();

// ── Helpers ───────────────────────────────────────────────────────────────────
function parseInWorker(file: File): Promise<RawDataset> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./data/GedcomParser.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (e: MessageEvent<{ dataset?: RawDataset; error?: string }>) => {
      worker.terminate();
      if (e.data.error) {
        reject(new Error(e.data.error));
      } else {
        resolve(e.data.dataset!);
      }
    };
    worker.onerror = (e) => {
      worker.terminate();
      reject(new Error(e.message));
    };
    file.text().then((text) => worker.postMessage({ gedcomText: text }));
  });
}

function collectLocations(
  graph: AncestorGraph,
  timelines: Map<string, TimelinePoint[]>,
): Location[] {
  const seen = new Set<Location>();
  for (const person of graph.persons.values()) {
    for (const event of person.events) {
      if (event.location) seen.add(event.location);
    }
  }
  // Household evidence on ancestor timelines — the only child births that are drawn
  for (const points of timelines.values()) {
    for (const point of points) {
      if (isEvidencePoint(point) && point.location) seen.add(point.location);
    }
  }
  return [...seen];
}

/**
 * normalized place key → every ancestor event that uses it, so the Needs Review
 * panel can say who uses each failed place. Household evidence (sibling and child
 * births on a timeline) is attributed to the ancestor whose timeline it is on.
 */
function buildPlaceUses(
  graph: AncestorGraph,
  timelines: Map<string, TimelinePoint[]>,
): Map<string, PlaceUse[]> {
  const uses = new Map<string, PlaceUse[]>();
  const add = (loc: Location | undefined, use: PlaceUse): void => {
    if (loc === undefined) {
      return;
    }
    const key = normalizer.normalize(loc.raw);
    if (key === null) {
      return;
    }
    const list = uses.get(key) ?? [];
    list.push(use);
    uses.set(key, list);
  };

  for (const person of graph.persons.values()) {
    const who = {
      personId: person.id,
      name: personDisplayName(person),
      wikitreeId: person.wikitreeId,
    };
    for (const event of person.events) {
      add(event.location, { ...who, event: event.type, year: event.year });
    }
    for (const point of timelines.get(person.id) ?? []) {
      if (isEvidencePoint(point)) {
        add(point.location, { ...who, event: point.kind, year: point.year });
      }
    }
  }
  return uses;
}

async function runPipeline(file: File, wikitreeIdOverride?: string): Promise<void> {
  // 1. Parse GEDCOM in worker
  const dataset = await parseInWorker(file);
  log.info(`Parsed GEDCOM — ${dataset.persons.size} persons`);

  // 2. Resolve root person
  const rootId = GraphBuilderService.resolveRootId(dataset, wikitreeIdOverride ?? '');
  if (rootId === null) {
    throw new Error('Root person not found. Try entering a WikiTree ID.');
  }

  // 3. Build ancestor graph
  const graph = graphBuilder.buildGraph(dataset, rootId);
  log.info(`Graph built — ${graph.persons.size} ancestors`);
  const timelines = timelineService.build(graph);
  const placeUses = buildPlaceUses(graph, timelines);

  // 4. Assign branches
  const { assignments, paths } = branchAssigner.assignBranches(graph);

  // 5. Generate arcs and dots (pre-geocode)
  const arcs = arcGenerator.generate(graph, assignments, timelines);
  const dotMap = dotAccumulator.accumulate(graph, assignments);

  // 6. Deliver first render — UploadScreen hides after this resolves
  AppStore.setState({
    graph,
    arcs,
    dotMap,
    branchPaths: paths,
    branchAssignments: assignments,
    geocodeFailures: [],
    geocodeUnverified: [],
  });
  TimelineStore.setState({ currentDecade: TimelineStore.getState().minDecade });
  log.info('First render delivered');

  // 7. Geocode locations — batch-coalesce AppStore updates every 500 ms
  const locations = collectLocations(graph, timelines);
  let batchTimer: ReturnType<typeof setTimeout> | null = null;

  await geocoderService.geocodeLocations(locations, {
    onProgress: (progress) => {
      AppStore.setState({ geocodeProgress: progress });
      if (batchTimer === null) {
        batchTimer = setTimeout(() => {
          batchTimer = null;
          // Regenerate dotMap with newly-geocoded coordinates, then push both
          // new references so Observable subscribers (ArcLayer, DotLayer) re-render.
          AppStore.setState({
            arcs: [...AppStore.getState().arcs],
            dotMap: dotAccumulator.accumulate(graph, assignments),
          });
        }, 500);
      }
    },
    onFailure: (raw) => {
      const uses = placeUses.get(normalizer.normalize(raw) ?? '') ?? [];
      AppStore.setState({
        geocodeFailures: [...AppStore.getState().geocodeFailures, { raw, uses }],
      });
    },
    onUnverified: (raw) => {
      const uses = placeUses.get(normalizer.normalize(raw) ?? '') ?? [];
      AppStore.setState({
        geocodeUnverified: [...AppStore.getState().geocodeUnverified, { raw, uses }],
      });
    },
  });

  // Final refresh after geocoding completes — regenerate dotMap with all resolved coords
  if (batchTimer !== null) {
    clearTimeout(batchTimer);
    batchTimer = null;
  }
  AppStore.setState({
    arcs: [...AppStore.getState().arcs],
    dotMap: dotAccumulator.accumulate(graph, assignments),
  });
  log.info('Geocoding complete');
}

// ── Mount components ──────────────────────────────────────────────────────────
const mapView = new MapView();
mapView.mount(appEl);

new ArcLayer(mapView).mount(appEl);
new WaypointLayer(mapView).mount(appEl); // below dots: a dot at the same place covers it
new DotLayer(mapView).mount(appEl);
new TimelineSlider().mount(controlsEl);
new GenerationCapSlider().mount(controlsEl);
new NeedsReviewPanel().mount(reviewPanelEl);
new BranchPanel().mount(sidebarBranchEl);
new BrickWallPanel(mapView).mount(sidebarBrickEl);

new UploadScreen({ onFile: (file, id) => runPipeline(file, id) }).mount(uploadScreenEl);

log.info('wikitree-timeline booted');
